import React from 'react';

import { getZonedDate } from '@eika-infrastruktur/date';
import { AttentionLabel } from '@eika/label';

import ANALYTICS_CATEGORIES from '../../../../analytics/categories';
import { useConfig } from '../../../../defaultConfig';
import { useTransferAccountsContext } from '../../../../hooks/useAccountState';
import useTranslation from '../../../../hooks/useTranslation';
import { AnalyticsEvents, trackEvent } from '../../../../services/analytics';
import { NoteTypes } from '../../../../translations/translationType';
import { AmountBox, AmountInput } from '../../../AmountBox';
import { NoteField } from '../../../NoteField';
import { AccountFreeWithdrawalLabel } from '../../../shared/AccountFreeWithdrawalLabel';
import { DateField } from '../../../shared/DateField';
import { FormFieldset } from '../../../shared/FormFieldset';
import { SelectList } from '../../../shared/SelectList';
import AccountField from '../../AccountField';
import { SetAccountType, TransferActionType } from '../form/actions';
import { isNoCoverage } from '../form/validate';
import useToAccount from '../hooks/useToAccount';
import FieldProps from './FieldProps';

const TranferMobileFields: React.FC<FieldProps> = ({
    form,
    locked,
    shouldFocusFirstElement,
    freeWithdrawals,
    date,
    allAccounts,
    validFromAccounts,
    invalidFromAccounts,
    accountState,
}) => {
    const { state, errors, handleChange, curryChange } = form;
    const translated = useTranslation();
    const { targetToAccount, targetFromAccount } = useTransferAccountsContext();
    const { toAccounts, selectedToAccount, isUnknownAccount } = useToAccount(state, targetToAccount, allAccounts);
    const { featureToggles } = useConfig();

    return (
        <FormFieldset disabled={locked}>
            <AmountBox isNoCoverage={isNoCoverage(state, targetFromAccount)} error={errors.amount} locked={locked}>
                <AmountInput
                    value={state.amount}
                    ariaLabel={translated.transferAmount}
                    onChange={(value) => handleChange({ type: TransferActionType.AMOUNT, payload: value })}
                    locked={locked}
                    focusFirst={shouldFocusFirstElement}
                    currency={targetToAccount?.currency}
                />
            </AmountBox>
            <SelectList>
                <li>
                    <AccountField
                        id="transferFromAccount"
                        placeholder={translated.transferFromAccount}
                        buttonLabel={translated.from}
                        title={translated.transferFromAccount}
                        account={targetFromAccount}
                        invalidAccounts={invalidFromAccounts}
                        accountNumber={state.fromAccount}
                        accounts={validFromAccounts ?? []}
                        error={errors.accountId}
                        onChange={(accountNumber) =>
                            handleChange({
                                type: TransferActionType.ACCOUNT_SELECTED,
                                payload: {
                                    setAccountType: SetAccountType.SET_FROM_ACCOUNT,
                                    accountNumber,
                                },
                            })
                        }
                        disabled={locked}
                        isLoading={accountState.isLoading}
                        loadingError={accountState.error}
                        refresh={accountState.refetch}
                        showModal={state.showFromAccounts}
                        setShowModal={(showModal: boolean) => {
                            if (showModal)
                                trackEvent(
                                    AnalyticsEvents.TRANSFER,
                                    ANALYTICS_CATEGORIES.TRANSFER,
                                    'select_from_account',
                                );

                            handleChange({
                                type: showModal
                                    ? TransferActionType.SHOW_FROM_ACCOUNTS
                                    : TransferActionType.HIDE_FROM_ACCOUNTS,
                            });
                        }}
                        locked={locked}
                        bottomLabel={
                            freeWithdrawals.show ? (
                                <AccountFreeWithdrawalLabel freeWithdrawals={freeWithdrawals.data} />
                            ) : undefined
                        }
                    />
                </li>
                <li>
                    <AccountField
                        id="transferToAccount"
                        placeholder={translated.transferToAccount}
                        buttonLabel={translated.to}
                        dataTestLabel="to"
                        title={translated.transferToAccount}
                        account={selectedToAccount}
                        accountNumber={state.toAccount}
                        accounts={toAccounts}
                        error={errors.toAccount}
                        onChange={(accountNumber) =>
                            handleChange({
                                type: TransferActionType.ACCOUNT_SELECTED,
                                payload: {
                                    setAccountType: SetAccountType.SET_TO_ACCOUNT,
                                    accountNumber,
                                },
                            })
                        }
                        disabled={locked}
                        isLoading={accountState.isLoading}
                        loadingError={accountState.error}
                        refresh={accountState.refetch}
                        showModal={state.showToAccounts}
                        setShowModal={(showModal: boolean) => {
                            if (showModal)
                                trackEvent(
                                    AnalyticsEvents.TRANSFER,
                                    ANALYTICS_CATEGORIES.TRANSFER,
                                    'select_to_account',
                                );

                            handleChange({
                                type: showModal
                                    ? TransferActionType.SHOW_TO_ACCOUNTS
                                    : TransferActionType.HIDE_TO_ACCOUNTS,
                            });
                        }}
                        locked={locked}
                        bottomLabel={
                            isUnknownAccount ? <AttentionLabel text={translated.unknownAccountWarning} /> : undefined
                        }
                    />
                </li>
                <li>
                    <NoteField
                        note={state.note}
                        ownText={state.ownText}
                        ownTextLabel={translated.transferAddOwnText}
                        labelText={translated.amountNote[NoteTypes.MESSAGE].label}
                        placeholder={translated.amountNote[NoteTypes.MESSAGE].placeholder}
                        onChange={curryChange(TransferActionType.NOTE)}
                        onChangeOwnText={
                            featureToggles.disableOwnTransferText || featureToggles.disableOwnText
                                ? undefined
                                : curryChange(TransferActionType.OWN_TEXT)
                        }
                        onShow={(show) => {
                            if (show) {
                                trackEvent(AnalyticsEvents.TRANSFER, ANALYTICS_CATEGORIES.TRANSFER, 'select_note');
                            }
                        }}
                        locked={locked}
                        forceDisableKid
                    />
                </li>
                <li>
                    <DateField
                        label={translated.date}
                        minDate={date.minDate}
                        date={state.transferDate ?? getZonedDate()}
                        onChange={(date) =>
                            handleChange({
                                type: TransferActionType.TRANSFER_DATE,
                                // https://jirasd.intra.eika.no/browse/NMBP-802
                                // pga. endring fra button til tekstfelt og hvis bruker manuelt oppgir
                                // ugyldig dato, blir "date" undefined.
                                // eslint-disable-next-line
                                // @ts-ignore
                                payload: date,
                            })
                        }
                        onShow={() => {
                            trackEvent(AnalyticsEvents.TRANSFER, ANALYTICS_CATEGORIES.TRANSFER, 'select_date');
                        }}
                        locked={locked}
                        warning={errors.transferDate}
                    />
                </li>
            </SelectList>
        </FormFieldset>
    );
};

export default TranferMobileFields;
